import React from "react"
import EChartsReact from "@/components/EChartsReact"
import moment from "moment"

/**
 * EUI Rolling Costs Charts 基础图表组件
 * 提供最基础的ECharts功能，接收原始数据作为props
 * 
 * @param {Object} props
 * @param {Array} props.electricityData - 电力数据数组，格式：[{month: 'YYYY-MM', costs: number, usage?: number}]
 * @param {Array} props.gasData - 燃气数据数组，格式：[{month: 'YYYY-MM', costs: number, usage?: number}]
 * @param {string} props.title - 图表标题
 * @param {string} props.className - CSS类名
 * @param {Object} props.style - 样式对象
 * @param {Object} props.customOptions - 自定义ECharts配置选项（可选）
 * @param {boolean} props.showPriceAxis - 是否显示价格轴（左侧Y轴）
 * @param {string} props.priceUnit - 价格单位，如 '$/kWh', '$/therm' 等
 */
export const EUIRollingCostsBaseChart = ({ 
  electricityData = [], 
  gasData = [], 
  title = 'Monthly Electricity and Gas Costs',
  className = '',
  style = { width: '100%', height: '100%' },
  customOptions = {},
  showPriceAxis = false,
  priceUnit = '$/Unit'
}) => {
  
  // 合并所有数据获取月份列表
  const allMonths = [...new Set([...electricityData, ...gasData].map(item => item.month))];
  
  // 计算价格数据（如果需要显示价格轴）
  const electricityPrices = showPriceAxis ? electricityData.map(item => {
    if (item.usage && item.usage > 0) {
      return item.costs / item.usage; // 计算单价：成本/用量
    }
    return 0;
  }) : [];
  
  const gasPrices = showPriceAxis ? gasData.map(item => {
    if (item.usage && item.usage > 0) {
      return item.costs / item.usage; // 计算单价：成本/用量
    }
    return 0;
  }) : [];
  
  // 配置Y轴
  const yAxisConfig = showPriceAxis ? [
    // 左侧价格轴
    {
      type: 'value',
      position: 'left',
      axisLabel: {
        formatter: value => `$${value.toFixed(3)}`
      },
      name: priceUnit,
      nameLocation: 'middle',
      nameGap: 40,
      nameTextStyle: {
        color: '#666',
        fontSize: 12
      }
    },
    // 右侧成本轴
    {
      type: 'value',
      position: 'right',
      axisLabel: {
        formatter: value => `$${(value/1000).toFixed(0)}K`
      },
      name: 'Total Costs',
      nameLocation: 'middle',
      nameGap: 40,
      nameTextStyle: {
        color: '#666',
        fontSize: 12
      }
    }
  ] : {
    type: 'value',
    position: 'right',
    axisLabel: {
      formatter: value => `$${(value/1000).toFixed(0)}K`
    }
  };
  
  // 配置系列数据
  const seriesConfig = [
    {
      data: electricityData.map(item => item.costs),
      type: 'bar',
      name: 'Electricity Costs',
      stack: 'total',
      yAxisIndex: showPriceAxis ? 1 : 0, // 使用右侧轴（成本轴）
      itemStyle: {
        color: '#4472c4',
        borderRadius: [0, 0, 0, 0]
      },
      barGap: 0
    },
    {
      data: gasData.map(item => item.costs),
      name: 'Gas Costs',
      type: 'bar',
      stack: 'total',
      yAxisIndex: showPriceAxis ? 1 : 0, // 使用右侧轴（成本轴）
      itemStyle: {
        color: '#c00000',
        borderRadius: [6, 6, 0, 0]
      },
      barGap: 0
    }
  ];
  
  // 如果显示价格轴，添加价格线图
  if (showPriceAxis) {
    seriesConfig.push(
      {
        data: electricityPrices,
        type: 'line',
        name: 'Electricity Price',
        yAxisIndex: 0, // 使用左侧轴（价格轴）
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          color: '#1890ff',
          width: 2
        },
        itemStyle: {
          color: '#1890ff'
        }
      },
      {
        data: gasPrices,
        type: 'line',
        name: 'Gas Price',
        yAxisIndex: 0, // 使用左侧轴（价格轴）
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          color: '#ff4d4f',
          width: 2
        },
        itemStyle: {
          color: '#ff4d4f'
        }
      }
    );
  }
  
  // 基础ECharts配置
  const baseOption = {
    backgroundColor: '#fff',
    title: {
      text: title,
      left: 'center',
      top: '2%',
      textStyle: {
        color: '#396598',
        fontSize: 14
      }
    },
    legend: {
      data: showPriceAxis 
        ? ['Electricity Costs', 'Gas Costs', 'Electricity Price', 'Gas Price']
        : ['Electricity', 'Gas'],
      left: 'center',
      top: '10%',
      itemWidth: 10,
      itemHeight: 10
    },
    xAxis: [
      {
        type: 'category',
        boundaryGap: true,
        data: allMonths,
        axisLabel: {
          rotate: 50,
          formatter: function(value) {
            return moment(value).format('MMM');
          }
        },
        position: 'bottom'
      },
      {
        type: 'category',
        position: 'top',
        boundaryGap: true,
        axisLine: {
          show: true
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          formatter: function(value) {
            return moment(value).format('YYYY');
          }
        },
        data: allMonths
      }
    ],
    yAxis: yAxisConfig,
    series: seriesConfig,
    grid: {
      top: '20%',
      left: showPriceAxis ? '10%' : '6%',
      right: showPriceAxis ? '10%' : '6%',
      bottom: '4%',
      containLabel: true
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const date = moment(params[0].axisValue).format('MMM YYYY');
        let result = `${date}<br/>`;
        
        let totalCosts = 0;
        params.forEach(param => {
          const value = param.value;
          const marker = param.marker;
          const seriesName = param.seriesName;
          
          if (seriesName.includes('Costs')) {
            const formattedValue = `$ ${Math.round(value).toLocaleString()}`;
            totalCosts += value;
            result += `${marker}${seriesName}: ${formattedValue}<br/>`;
          } else if (seriesName.includes('Price')) {
            const formattedValue = `$ ${value.toFixed(3)}`;
            result += `${marker}${seriesName}: ${formattedValue}<br/>`;
          }
        });
        
        if (totalCosts > 0) {
          result += `Total Costs: $ ${Math.round(totalCosts).toLocaleString()}`;
        }
        
        return result;
      }
    }
  };

  // 合并自定义配置
  const finalOption = { ...baseOption, ...customOptions };

  return (
    <EChartsReact 
      className={className} 
      option={finalOption} 
      style={style} 
    />
  );
};
